import { useMemo } from "react";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { usePaginatedLogs } from "../hooks/usePaginatedLogs";
import { getDateHeader, groupAndSortLogsByDate } from "../utils";
import { FilterState } from "src/hooks/useAuditLogFilter";
import { Log } from "src/types.g";
import { EmptyState } from "../EmptyState";
import { Loader } from "@screencloud/screencloud-ui-components";
import { format } from "date-fns";
import { LogItem } from "../LogItem";
import { FetchingLoaderStyled, Styled } from "./styles";

interface LogListProps {
  filters: FilterState;
  onLogItemClick: (log: Log) => void;
}

export const LogList = (props: LogListProps): JSX.Element => {
  const { logs, loading, hasMore, loadMore, data } = usePaginatedLogs({
    filters: props.filters,
  });
  const { isAtBottom, refCallback } = useInfiniteScroll({
    loading,
    hasMore,
    loadMore,
  });
  // Memoize grouped/sorted logs for performance
  const sortedGroupedLogsByDate = useMemo(
    () => groupAndSortLogsByDate(logs),
    [logs],
  );

  if (loading && !data) {
    return (
      <FetchingLoaderStyled>
        <Loader
          active
          inline
          style={{ position: "absolute", top: "53.9%", left: "48.7%", zIndex: 0 }}
        />
      </FetchingLoaderStyled>
    );
  }

  if (data && data.allLogsV3 == null) {
    return <EmptyState status="error" />;
  }

  if (!data?.allLogsV3?.nodes || data.allLogsV3.nodes.length === 0) {
    return <EmptyState status="no-results" />;
  }

  return (
    <Styled>
      {Object.keys(sortedGroupedLogsByDate).map((groupKey) => (
        <div key={groupKey} className="log-group">
          <div className="date-header">
            <span className="date-label">
              {getDateHeader(sortedGroupedLogsByDate[groupKey][0].timestamp)}
            </span>
            <span className="date-full">
              {format(new Date(groupKey), "MMM d, yyyy")}
            </span>
          </div>
          <table className="log-items">
            <colgroup>
              <col span={1} style={{ width: "50%" }} />
              <col span={1} style={{ width: "20%" }} />
              <col span={1} style={{ width: "30%" }} />
            </colgroup>
            <tbody>
              {sortedGroupedLogsByDate[groupKey].map((log) => (
                <LogItem
                  key={log.id}
                  log={log}
                  onLogItemClick={props.onLogItemClick}
                />
              ))}
            </tbody>
          </table>
        </div>
      ))}
      <div className="loader-container" ref={refCallback}>
        {hasMore && isAtBottom && <Loader active size="small" inline />}
      </div>
    </Styled>
  );
};
